import { z } from 'zod'
import { api } from '@/shared/api/client'
import type { Translate } from '@/shared/api/errors'
import { uuidSchema } from '@/shared/api/schemas'
import { seededName } from '@/shared/lib/seededName'

export const REVENUE_KINDS = ['INTERNET', 'EQUIPMENT', 'OTHER'] as const
export type RevenueKind = (typeof REVENUE_KINDS)[number]

export const PAYMENT_CATEGORY_CODE = /^[A-Z][A-Z0-9_]{1,31}$/
export const PAYMENT_CATEGORY_NAME_MAX = 100
export const PAYMENT_CATEGORY_PRIORITY_MAX = 1000

const paymentCategorySchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  priority: z.number(),
  revenueKind: z.enum(REVENUE_KINDS),
  system: z.boolean(),
  active: z.boolean(),
})

export type PaymentCategory = z.infer<typeof paymentCategorySchema>

export interface PaymentCategoryDraft {
  code: string
  name: string
  priority: number
  revenueKind: RevenueKind
  active: boolean
}

export async function fetchPaymentCategories(signal?: AbortSignal): Promise<PaymentCategory[]> {
  const response = await api.get<unknown>('/api/payment-categories', { ...(signal ? { signal } : {}) })
  return z.array(paymentCategorySchema).parse(response.data)
}

export async function createPaymentCategory(draft: PaymentCategoryDraft): Promise<PaymentCategory> {
  const response = await api.post<unknown>('/api/payment-categories', draft)
  return paymentCategorySchema.parse(response.data)
}

export async function updatePaymentCategory(id: string, draft: PaymentCategoryDraft): Promise<PaymentCategory> {
  const response = await api.put<unknown>(`/api/payment-categories/${id}`, draft)
  return paymentCategorySchema.parse(response.data)
}

export function paymentCategoryName(category: Pick<PaymentCategory, 'code' | 'name'>, t: Translate): string {
  return seededName('paymentCategories', category.code, category.name, t)
}
